import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, map, take, timer } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, IAppConfig } from '@app/core/config';
import { KeyValuePair, TablePagingIndex } from '@app/core/models';
import { UtilsService } from '@app/core/services/utils.service';
import { NotificationView } from '..//models';

import { NavigationExtras, Router } from '@angular/router';
import { LogHelper } from '@app/shared/helper';
import { ServiceGroupType } from '@app/shared/services';
import { NotificationBoxType, NotificationType } from '../types';
import { AnnouncementService } from '@app/views/pages/announcement/service';

@Injectable({
	providedIn: 'root'
})
export class NotificationService {

	private refreshNotificationSubscription: Subscription | null = null;
	private notificationCountSource: BehaviorSubject<number>;
	private announcementCountSource: BehaviorSubject<number>;
	private friendRequestsCountSource: BehaviorSubject<number>;
	private messagesCountSource: BehaviorSubject<number>;
	private httpSendSubscription: Subscription;
	private httpNewSendSubscription: Subscription;

	private filteredNotificationsCountSource = new BehaviorSubject<number>(0);
	public filteredNotificationsCount$ = this.filteredNotificationsCountSource.asObservable();

	private filterSource = new Subject<KeyValuePair[]>();
	public filter$ = this.filterSource.asObservable();

	// temp data stored
	notificationsTempData: NotificationView[];
	messagesTempData: NotificationView[];
	friendRequestTempData: NotificationView[];

	typeForClearAll: number;

	constructor(
		@Inject(APP_CONFIG) private appConfig: IAppConfig,
		private http: HttpClient,
		private router: Router,
		private utilsService: UtilsService,
		private announcementService: AnnouncementService
	) {
		this.notificationCountSource = new BehaviorSubject(0);
		this.friendRequestsCountSource = new BehaviorSubject(0);
		this.messagesCountSource = new BehaviorSubject(0);
		this.announcementCountSource = new BehaviorSubject(0);
	}

	updateFilter(filters: KeyValuePair[]): void {
		this.filterSource.next(filters);
	}

	getNotifications(type: NotificationBoxType): Observable<any> {
		// const url = `${this.appConfig.apiEndpoint}/v2/Notification/Web/Notify/${type}`;
		const notifEnum = NotificationBoxType[type];
		const url = `${this.appConfig.apiEndpoint}/notification/v2/notifications/${notifEnum}`;
		return this.http.get<any>(url).pipe(
			map(response => {

				switch (type) {
					case NotificationBoxType.Notification:
						this.notificationsTempData = response['data'];
						break;

					case NotificationBoxType.Message:
						this.messagesTempData = response['data'];
						break;

					case NotificationBoxType.Friend:
						this.friendRequestTempData = response['data'];
						break;
				}

				return response['data'] || []
			})
		)
	}

	getMessages(): Observable<NotificationView> {
		const url = `${this.appConfig.apiEndpoint}/v2/Notification/Notify/Messages`;

		return this.http.get<any>(url).pipe(
			map(response => {
				this.notificationsTempData = response['data'];
				return response['data'] || []
			})
		)
	}


	getNotifyCount(): void {
		// const url = `${this.appConfig.apiEndpoint}/v2/Notification/Web/Notify/Count`;
		const url = `${this.appConfig.apiEndpoint}/notification/v2/notifications/count`;

		this.httpNewSendSubscription = this.http.get<any>(url).pipe(
			map(response => response['data'] || [])
		).subscribe((result: any[]) => {
			this.updateNotificationCount(result['notification']);
			this.updateFreindsRequestCount(result['friend']);
			this.updateMessagesCount(result['message']);
			this.notificationsTempData = null;
			this.messagesTempData = null;
			this.friendRequestTempData = null;
			this.httpNewSendSubscription.unsubscribe();
		});
	}


	getAnnouncementCount(): void {
		const url = `${this.appConfig.apiEndpoint}/new-core/announcements/count`;

		this.httpSendSubscription = this.http.get<any>(url).pipe(
			map(response => response['data'] || 0)
		).subscribe((result: number) => {
			this.updateAnnouncementCount(result);
			this.announcementService.announcementsTempData = null;
			this.httpSendSubscription.unsubscribe();
		});
	}


	scheduleNotification(): void {
		const timerSource$ = timer(1, this.appConfig.notificationRefreshTime);
		this.refreshNotificationSubscription = timerSource$.subscribe(() => {
			this.getAnnouncementCount();
		});
	}

	deleteRefreshNotificationTimer(): void {
		if (this.refreshNotificationSubscription) {
			this.refreshNotificationSubscription.unsubscribe();
		}
	}

	filters(): Observable<KeyValuePair[]> {
		return this.filterSource.asObservable();
	}

	notificationCount(): Observable<number> {
		return this.notificationCountSource.asObservable();
	}

	messagesCount(): Observable<number> {
		return this.messagesCountSource.asObservable();
	}

	announcementCount(): Observable<number> {
		return this.announcementCountSource.asObservable();
	}

	freindRequestsCount(): Observable<number> {
		return this.friendRequestsCountSource.asObservable();
	}

	updateNotificationCount(count: number): void {
		this.notificationCountSource.next(count);
	}

	updateFreindsRequestCount(count: number): void {
		this.friendRequestsCountSource.next(count);
		if (count === 0) {
			this.friendRequestTempData = null;
		}
	}

	updateMessagesCount(count: number): void {
		this.messagesCountSource.next(count);
	}

	updateAnnouncementCount(count: number): void {
		this.announcementCountSource.next(count);
	}

	resetNotification(): void {
		this.deleteRefreshNotificationTimer();
		this.scheduleNotification();
	}


	// getTopUserNotification(): void {
	// 	const url = `${this.appConfig.apiEndpoint}/v2/Notification/Notify`;

	// 	this.httpSendSubscription = this.http.get<any>(url).pipe(
	// 		map(response => response['data'] || [])
	// 	).subscribe((result: any[]) => {

	// 		this.updateNotification(result['notifications']);
	// 		this.updateMesssage(result['messages']);
	// 		this.updateFreindRequest(result['friends']);
	// 		this.httpSendSubscription.unsubscribe();
	// 	});
	// }

	getAllUserNotification(
		pageNumber: number, pageSize: number, filters: KeyValuePair[], sortDirection: string
	): Observable<TablePagingIndex<NotificationView>> {
		const url = `${this.appConfig.apiEndpoint}/v2/Notification`;
		const params = this.utilsService.generateHttpParamForTablePaging(pageNumber, pageSize, filters, sortDirection);
		return this.http.get<TablePagingIndex<NotificationView>>(url, { params }).pipe(
			map(response => response['data'] || ({} as TablePagingIndex<NotificationView>))
		);
	}

	setRead(id: string): void {
		// const url = `${this.appConfig.apiEndpoint}/v2/Notification/${id}`;
		const url = `${this.appConfig.apiEndpoint}/notification/v2/notifications/${id}`;
		const payload = {
			"data": id
		};
		this.http.delete<number>(url, { body: payload }).pipe().subscribe();
	}

	onNotificationCick(notification: NotificationView, isListPage: boolean, count?: number): void {
		if (!notification.isSeen) {
			let updatedCount: number | undefined = count;
			if (count) {
				updatedCount = --count;
				this.updateNotificationCount(updatedCount);
			} else {
				this.notificationCountSource.pipe(take(1))
					.subscribe(
						res => {
							updatedCount = res > 0 ? --res : 0;
							this.updateNotificationCount(updatedCount);
						}
					)
			}
			if (isListPage === false) {
				const index = this.notificationsTempData.indexOf(notification);
				if (index > -1) {
					this.notificationsTempData.splice(index, 1);
				}
			}
			this.setRead(notification.id);
		}
		switch (+NotificationType[notification.notificationType]) {
			case NotificationType.VerifyInfo:
				this.verifyNotification(notification);
				break;

			case NotificationType.VerifyBankAccount:
				this.handleRouterNavigation(`/my-accounts`);
				break;

			case NotificationType.Friend:
				this.FriendNotification(notification);
				break;

			case NotificationType.WalletChange:
				this.handleRouterNavigation(`/wallets/transactions`);
				break;

			case NotificationType.Deposit:
				this.DepositNotification(notification);
				break;

			case NotificationType.Withdrawal:
				this.WithdrawalNotification(notification);
				break;

			case NotificationType.Point:
				this.handleRouterNavigation(`/points/transactions/list`);
				break;

			case NotificationType.PointChange:
				this.handleRouterNavigation(`/points/transactions/list`);
				break;

			case NotificationType.Receive:
				this.ReceiveNotification(notification);
				break;

			case NotificationType.Exchange:
				this.ExchangeServiceNotification(notification);
				break;

			case NotificationType.Voucher:
				this.VoucherServiceNotification(notification);
				break;

			case NotificationType.Crypto:
				this.handleRouterNavigation(notification.dataUrl);
				break;

			case NotificationType.CryptoWithdraw:
				this.handleRouterNavigation(notification.dataUrl);
				break;

			case NotificationType.Send:
				this.handleRouterNavigation(`/send/money-currency/${notification.dataUrl}/${notification.relatedId}`);
				break;

			case NotificationType.DirectDeposit:
				this.handleRouterNavigation(`/deposit/transactions/direct-deposit/${notification.relatedId}`);
				break;
				
			case NotificationType.Chat:
				if (notification.subType[0] === 'P2P') {
					this.handleRouterNavigation(`/p2p/${notification.subType[1]}/chat`);
				}
				break;

			default:
				break
		}
	}


	private verifyNotification(notification: NotificationView): void {
		this.handleRouterNavigation('/profile/account-info');
	}

	private ReceiveNotification(notification: NotificationView): void {
		this.handleRouterNavigation(`/receive/${notification.dataUrl}/${notification.relatedId}`);
	}

	private FriendNotification(notification: NotificationView): void {
		this.router.navigate(['/friends']);
	}

	private DepositNotification(notification: NotificationView): void {
		const subType = JSON.parse(notification['originalSubType']);
		if (subType['IsElectronicMoneyRequest']) {
			if (subType['ServiceGroupType'] === ServiceGroupType.EMoney) {
				this.handleRouterNavigation(`/deposit/electronic-money/${notification.relatedId}`);
				return;
			}
			this.handleRouterNavigation(`/deposit/crypto/${notification.relatedId}`);
		} else {
			this.handleRouterNavigation(`/deposit/money-currency/${notification.dataUrl}/${notification.relatedId}`);
		}
	}

	private WithdrawalNotification(notification: NotificationView): void {
		const subType = JSON.parse(notification['originalSubType']);
		if (subType['IsElectronicMoneyRequest']) {
			if (subType['ServiceGroupType'] === ServiceGroupType.EMoney) {
				this.handleRouterNavigation(`/withdrawal/electronic-money/${notification.relatedId}`);
				return;
			}
			this.handleRouterNavigation(`/withdrawal/crypto/${notification.relatedId}`);
		} else {
			this.handleRouterNavigation(`/withdrawal/money-currency/${notification.dataUrl}/${notification.relatedId}`);
		}
	}

	private ExchangeServiceNotification(notification: NotificationView): void {
		this.handleRouterNavigation(`/exchange/service/${notification.relatedId}`);
	}

	private VoucherServiceNotification(notification: NotificationView): void {
		this.handleRouterNavigation(`/voucher/${notification.relatedId}`);
	}

	private handleRouterNavigation(route: string, navigationExtras: NavigationExtras = null): void {


		const url = this.router.url.replace('%3D', '=');

		LogHelper.log('NOTIFICATION => ', url);


		if (url.indexOf(route) !== -1 || (url.indexOf('profile') !== -1 && route.indexOf('profile') !== -1)) {
			this.router.navigate(['/redirecting']);
		}

		setTimeout(() => {
			if (navigationExtras) {
				this.router.navigate([route], navigationExtras);
			} else {
				this.router.navigate([route]);
			}
		}, 1);
	}

	clearAll(type: number): Observable<any> {
		// type for method e.g deposit-p2p,withdrawal , ...
		// const url = `${this.appConfig.apiEndpoint}/v2/Notification/DeleteAll/${type}`;
		const notifEnum = NotificationBoxType[type];
		const url = `${this.appConfig.apiEndpoint}/notification/v2/notifications/all/${notifEnum}`;
		const payload = {
			"data": notifEnum
		};
		return this.http.delete(url, { body: payload }).pipe(
			map(response => {
				this.notificationsTempData = null;
				return response['data']
			}
			)
		);
	}
}
